import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../Stores/Actions/userAction';
import LoginPopup from './Login/LoginPopup';
import { HiMenu, HiX } from 'react-icons/hi';
import Button from './ui/Button';
import SocialLinks from './ui/SocialLinks';
import { clearPendingBookingCache } from '../utils/pendingBooking';
import { logoHindustanTimes } from '../data/pressOutlets';

const Mark = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="12.5" stroke="#75581F" strokeWidth="1.2" />
    <circle cx="14" cy="14" r="3.5" fill="#75581F" />
    <circle cx="14" cy="3" r="1.2" fill="#10352E" />
    <circle cx="25" cy="14" r="1.2" fill="#10352E" />
  </svg>
);

const Navbar = () => {
  const { tab } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const loginToken = localStorage.getItem('loginToken');
  const profileMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [formData, setFormData] = useState({});
  const [openaccountPopup, setOpenaccountPopup] = useState(false);
  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const api = axios.create({
    baseURL: `${process.env.REACT_APP_FRONTEND_URL}`,
  });

  api.interceptors.request.use(
    (config) => {
      if (loginToken) {
        config.headers.Authorization = `Bearer ${loginToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  useEffect(() => {
    const userData = async () => {
      try {
        const response = await api.post(`${process.env.REACT_APP_SERVER_URL}/getUser`);
        setFormData(response.data.data);
      } catch (error) {
        localStorage.clear();
        console.error(error);
      }
    };
    if (loginToken) {
      userData();
    }
  }, [loginToken]);

  useEffect(() => {
    dispatch(getUserDetails(formData));
  }, [dispatch, formData]);

  useEffect(() => {
    const firstPathSegment = location.pathname.replace(/^\//, '').split('/')[0];
    setActiveMenuItem(firstPathSegment || tab || 'Home');
  }, [location.pathname, tab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setOpenaccountPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem);
    setMobileMenuOpen(false);
    setOpenaccountPopup(false);
    navigate(`/${menuItem}`);
  };

  const logOut = () => {
    toast('Logged out successfully', { type: 'success' });
    localStorage.clear();
    clearPendingBookingCache();
    setFormData({});
    setOpenaccountPopup(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  // "Book Session" is the call-to-action button now, so it is not repeated in the text links.
  const navItems = [
    { id: 'Home', label: 'Home', path: '' },
    { id: 'services', label: 'Services', path: 'services' },
    { id: 'blog', label: 'Blog', path: 'blog' },
    { id: 'seminar', label: 'Seminar', path: 'seminar' },
    { id: 'media-profile', label: 'About', path: 'media-profile' },
    { id: 'session-history', label: 'My Sessions', path: 'session-history' },
    { id: 'contactUs', label: 'Contact', path: 'contactUs' },
  ];

  const isLoggedIn = formData && Object.keys(formData).length > 0;

  const accountButton = 'w-full rounded-none border border-brand-border/70 bg-white px-4 py-3 text-left text-sm font-semibold text-brand-dark transition hover:border-brand-primary hover:bg-brand-tint';

  return (
    <>
      {/* thin strip above the navigation: the recognition, and Poonam's real channels so visitors can see she is legit */}
      <div className="bg-forest px-4 py-1.5 text-canvas sm:py-2.5">
        <div className="mx-auto flex max-w-container flex-col items-center gap-2 sm:flex-row sm:justify-between sm:px-4 lg:px-6">
          <p className="hidden text-center text-[10px] font-medium uppercase tracking-[0.2em] text-canvas/85 sm:block sm:text-left sm:text-[11px]">
            Named one of the <span className="text-amber">Top 10 Astrologers of India</span> by Hindustan Times
          </p>
          <div className="flex flex-shrink-0 items-center gap-3">
            <span className="hidden whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.2em] text-canvas/60 lg:inline">Follow Poonam</span>
            <SocialLinks tone="dark" className="flex-nowrap" />
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 border-b transition-all duration-500 ${
          scrolled ? 'border-brand-border/70 bg-canvas/90 shadow-elevation-sm backdrop-blur-md' : 'border-brand-border/40 bg-canvas'
        }`}
      >
        <nav className={`mx-auto flex max-w-container items-center justify-between gap-4 px-5 sm:px-8 lg:px-10 transition-all duration-500 ${scrolled ? 'py-2.5' : 'py-4'}`}>
          <button type="button" className="group flex items-center gap-2.5" onClick={() => navigate('/')} aria-label="Bhavishyavani home">
            <span className="transition-transform duration-700 group-hover:rotate-[360deg]"><Mark /></span>
            <span className="font-serif text-xl italic text-brand-dark">Bhavishyavani</span>
          </button>

          <ul className="hidden items-center gap-7 xl:flex">
            {navItems.map((item) => {
              const active = activeMenuItem === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleMenuItemClick(item.path)}
                    aria-current={active ? 'page' : undefined}
                    className={`group relative py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                      active ? 'text-brand-primary' : 'text-brand-dark/60 hover:text-brand-dark'
                    }`}
                  >
                    {item.label}
                    <span
                      className={`absolute inset-x-0 -bottom-0.5 h-[2px] origin-left bg-brand-accent transition-transform duration-500 ease-premium ${
                        active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline-dark"
              className="hidden !px-5 !py-2.5 xl:inline-flex"
              onClick={() => setOpenaccountPopup(!openaccountPopup)}
            >
              {isLoggedIn ? 'My Profile' : 'Sign In'}
            </Button>
            <Button
              type="button"
              variant="primary-dark"
              className="hidden !px-6 !py-2.5 xl:inline-flex"
              onClick={() => handleMenuItemClick('book')}
            >
              Book Now
            </Button>

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-dark/25 text-brand-dark transition hover:border-brand-dark xl:hidden"
              aria-label="Open menu"
              onClick={() => setMobileMenuOpen(true)}
            >
              <HiMenu size={20} />
            </button>
          </div>
        </nav>
        {openaccountPopup && (
          <div ref={profileMenuRef} className="animate-fade-up absolute right-6 top-full z-[60] mt-2 hidden w-[min(92vw,280px)] xl:block" style={{ opacity: 0 }}>
            {isLoggedIn ? (
              <div className="rounded-none border border-brand-border/70 bg-surface p-5 shadow-elevation-md">
                <div className="mb-4 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-muted">My Profile</div>
                <ul className="space-y-2.5">
                  <li><button type="button" className={accountButton} onClick={() => navigate('/Account')}>My Account Details</button></li>
                  <li><button type="button" className={accountButton} onClick={() => navigate('/payments')}>My Payments</button></li>
                  <li><button type="button" className={accountButton} onClick={() => navigate('/support')}>Help &amp; Support</button></li>
                  <li><button type="button" className={accountButton} onClick={logOut}>Log Out</button></li>
                </ul>
              </div>
            ) : (
              <div className="rounded-none border border-brand-border/70 bg-surface p-5 text-center shadow-elevation-md">
                <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-muted">Welcome to Bhavishyavani</div>
                <Button type="button" variant="primary-dark" className="w-full" onClick={() => setLoginPopupOpen(true)}>
                  Log In
                </Button>
              </div>
            )}
          </div>
        )}

      </header>

      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-canvas xl:hidden">
          <div className="flex items-center justify-between border-b border-brand-border/60 px-5 py-4 sm:px-8">
            <button type="button" className="flex items-center gap-2.5" onClick={() => { setMobileMenuOpen(false); navigate('/'); }}>
              <Mark />
              <span className="font-serif text-xl italic text-brand-dark">Bhavishyavani</span>
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-dark/25 text-brand-dark"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
            >
              <HiX size={20} />
            </button>
          </div>

          <div className="mx-auto flex w-full max-w-container flex-1 flex-col px-5 py-8 sm:px-8">
            <div className="flex flex-col">
              {navItems.map((item, index) => {
                const active = activeMenuItem === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleMenuItemClick(item.path)}
                    className={`animate-fade-up flex w-full items-baseline justify-between border-b border-brand-border/60 py-4 text-left transition ${
                      active ? 'text-brand-primary' : 'text-brand-dark/80 hover:text-brand-dark'
                    }`}
                    style={{ opacity: 0, animationDelay: `${60 + index * 55}ms` }}
                  >
                    <span className="font-serif text-2xl italic">{item.label}</span>
                    <span className="text-xs text-brand-accent" aria-hidden="true">{active ? '●' : '→'}</span>
                  </button>
                );
              })}
            </div>

            <Button
              type="button"
              variant="primary-dark"
              className="animate-fade-up mt-8 w-full"
              style={{ opacity: 0, animationDelay: '480ms' }}
              onClick={() => handleMenuItemClick('book')}
            >
              Book Your Session
            </Button>

            <div className="mt-8 border-t border-brand-border/60 pt-6">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-muted">Account</p>

              {isLoggedIn ? (
                <div className="space-y-2">
                  <button type="button" className={accountButton} onClick={() => { setMobileMenuOpen(false); navigate('/Account'); }}>My Account Details</button>
                  <button type="button" className={accountButton} onClick={() => { setMobileMenuOpen(false); navigate('/payments'); }}>My Payments</button>
                  <button type="button" className={accountButton} onClick={() => { setMobileMenuOpen(false); navigate('/support'); }}>Help &amp; Support</button>
                  <button type="button" className={accountButton} onClick={logOut}>Log Out</button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline-dark"
                  className="w-full"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setLoginPopupOpen(true);
                  }}
                >
                  Log In
                </Button>
              )}
            </div>

            <div className="mt-auto flex items-center gap-3 pt-10 text-xs text-brand-muted">
              <img src={logoHindustanTimes} alt="Hindustan Times" className="h-4 w-auto" />
              <span>Top 10 Astrologers of India</span>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
      {loginPopupOpen && <LoginPopup onClose={() => setLoginPopupOpen(false)} />}
    </>
  );
};

export default Navbar;
